import type { ToolResponse } from '@/types/chat'
import type { ToolEffectContext } from './types'

// Import all tool effect registries
import placeToolEffects from './place-tool/registry'
// Future imports:
// import imageryToolEffects from './imagery-tool/registry'
// import transportationToolEffects from './transportation-tool/registry'

/**
 * Central registry aggregating all tool effect handlers.
 * Each tool's registry is imported and spread into this object.
 *
 * To add a new tool effect:
 * 1. Create a new directory: `{tool-name}-tool/`
 * 2. Create `{tool-name}-tool/registry.ts` with effect handler
 * 3. Import it above: `import fooToolEffects from './foo-tool/registry'`
 * 4. Add it to the registry below: `...fooToolEffects,`
 */
const TOOL_EFFECTS_REGISTRY = {
  ...placeToolEffects,
  // Future tool effects will be added here
  // ...imageryToolEffects,
  // ...transportationToolEffects,
}

/**
 * Execute registered effect handlers for a completed tool call.
 *
 * This function:
 * 1. Looks up the handler for the given tool name
 * 2. Parses the JSON result from the tool response
 * 3. Calls the handler with the parsed result and context
 * 4. Handles errors gracefully with logging
 *
 * If no handler is registered for the tool, this is a no-op.
 * This allows tools to exist without effects.
 *
 * @param toolName - The name of the tool that completed
 * @param toolResponse - The tool response containing result data
 * @param context - Context object with controllers (map, gallery, etc.)
 *
 * @example
 * ```tsx
 * const effectContext = useToolEffectContext()
 *
 * useEffect(() => {
 *   if (toolCompleted && toolResponse) {
 *     executeToolEffects(toolName, toolResponse, effectContext)
 *   }
 * }, [toolCompleted, toolName, toolResponse, effectContext])
 * ```
 */
export function executeToolEffects(
  toolName: string,
  toolResponse: ToolResponse,
  context: ToolEffectContext
): void {
  const handler = TOOL_EFFECTS_REGISTRY[toolName]

  if (!handler) {
    // No effects registered for this tool - this is normal
    return
  }

  try {
    // Parse the JSON result from the tool response
    const result = JSON.parse(toolResponse.content || '{}')

    // Execute the handler with parsed result and context
    handler(result, context)
  } catch (error) {
    console.error(
      `[tool-effects] Failed to execute effects for tool "${toolName}":`,
      error
    )
  }
}

// Export public API
export * from './types'
export { ToolEffectProvider, useToolEffectContext } from './ToolEffectContext'
